import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import subscriptionApi, {
  SubscriptionDetails,
  SUBSCRIPTION_STATUS,
} from '../services/subscriptionApi';
import apiService from '../services/api';
import { TierConfigurator, CancelSubscriptionModal, ReactivateSubscriptionModal } from '../components/subscription';
import { usePaymentAttempt } from '../services/idempotency';
import MAUUsage from '../components/subscription/MAUUsage';
import AssignBillingProfileModal from '../components/AssignBillingProfileModal';
import LifetimeAddonStoreCore from '../components/LifetimeAddonStoreCore';
import { reviewIncentivesApi } from '../services/reviewIncentivesApi';
import type {
  LifetimeReviewSubmission,
  LifetimeReviewCredit,
  CanSubmitInfo,
  EligibleWorkspace,
} from '../widget/types';
import { useToast } from '../context/ToastContext';
import { useAuth } from '../context/AuthContext';
import { useSoftLaunch } from '../hooks';
import { useMyCampaign } from '../hooks/useMyCampaign';
import {
  ChevronRight,
  Rocket,
  AlertTriangle,
  Clock,
  CreditCard,
  Users,
  Eye,
  Plug,
  Calendar,
  Globe,
  Palette,
  Database,
  Check,
  X,
  Star,
  Gift,
  Infinity,
  RefreshCw,
  Ticket,
  ArrowRight,
  Package,
  ArrowRightLeft,
} from 'lucide-react';

// Feature code to human-readable name mapping
const FEATURE_NAMES: Record<string, string> = {
  '100': 'Data Tags & Row-Level Security',
  '101': 'Raw SQL Queries',
  '102': 'Email Scheduling',
  '103': 'Report Embedding',
  '104': 'API Access',
  '105': 'Login Page Branding',
  '110': 'Stored Procedures',
  '111': 'Phrase Manager',
  '112': 'FastCache (Dashboard Caching)',
  '113': 'Server Sync',
  '114': 'Cisco DUO 2FA',
  '115': 'Per-Tenant Datasource',
  '116': 'SSO',
  '118': 'TLS Certificate Management',
  '119': 'ODBC Drivers (Tier 2 — Custom Uploads)',
  '120': 'SSH Tunnel',
  '121': 'Static Egress IP',
  '122': 'Native Connections',
};

const SubscriptionPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const workspaceId = parseInt(id || '0', 10);
  const { showSuccess, showError } = useToast();
  const { isEnabled: isSoftLaunch, trialDays, messaging: softLaunchMessaging } = useSoftLaunch();
  const [workspace, setWorkspace] = useState<any>(null);
  const [subscription, setSubscription] = useState<SubscriptionDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
const { agencyRole } = useAuth();
const addonAttempt = usePaymentAttempt();
  const canManageSubscription = agencyRole === 'owner' || agencyRole === 'billing_admin';
const [activeTab, setActiveTab] = useState<'plan' | 'usage' | 'change' | 'addons'>(() => {
    const state = location.state as { tabIndex?: number } | null;
    return state?.tabIndex === 2 ? 'change' : 'plan';
  });

  // Campaigns are first paid subscription only: skip for paid or lifetime workspaces.
  const myCampaign = useMyCampaign(
    !!subscription &&
    !subscription.chargebee_subscription_id &&
    ((subscription as any).subscription_status ?? (subscription as any).status) !== 'lifetime'
  );

  // Modals
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [assignBillingModalOpen, setAssignBillingModalOpen] = useState(false);
  const [reactivateModalOpen, setReactivateModalOpen] = useState(false);

// Lifetime addons
  const [lifetimeAddons, setLifetimeAddons] = useState<any[]>([]);
  const [myLifetimeAddons, setMyLifetimeAddons] = useState<any[]>([]);
  const [addonsLoading, setAddonsLoading] = useState(false);

  // Phase 9 review incentive state
  const [reviewSubmissions, setReviewSubmissions] = useState<LifetimeReviewSubmission[]>([]);
  const [reviewCredits, setReviewCredits] = useState<LifetimeReviewCredit[]>([]);
  const [canSubmitReview, setCanSubmitReview] = useState<CanSubmitInfo>({ canSubmit: false, reason: null, unlocksAt: null });
  const [eligibleWorkspaces, setEligibleWorkspaces] = useState<EligibleWorkspace[]>([]);

  // Lifetime deal
  const [lifetimeCode, setLifetimeCode] = useState('');
  const [lifetimeLoading, setLifetimeLoading] = useState(false);
  const [lifetimeError, setLifetimeError] = useState<string | null>(null);
  const [redeemedCodes, setRedeemedCodes] = useState<any[]>([]);

  // Trial extension
  const [extendLoading, setExtendLoading] = useState(false);

  // Hover states
  const [hoveredTab, setHoveredTab] = useState<string | null>(null);

  useEffect(() => {
    if (workspaceId) {
      loadData();
    }
  }, [workspaceId, (location.state as any)?.refresh]);

  const loadData = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [wsResponse, subData] = await Promise.all([
        apiService.get(`/workspaces/${workspaceId}`),
        subscriptionApi.getSubscription(workspaceId),
      ]);
setWorkspace(wsResponse.data);
      setSubscription(subData);

      if (((wsResponse.data?.lifetime_code_count) || 0) > 0) {
        setAddonsLoading(true);
        try {
const [available, mine] = await Promise.all([
          subscriptionApi.listLifetimeAddons(workspaceId),
          subscriptionApi.getMyLifetimeAddons(workspaceId),
        ]);
        setLifetimeAddons(available);
        setMyLifetimeAddons(mine);
        } catch (err) {
          // non-fatal
        } finally {
          setAddonsLoading(false);
        }

        // Phase 9 review data — user-scoped, fetched alongside lifetime addons
        try {
          const [subs, credits, canSub, workspaces] = await Promise.all([
            reviewIncentivesApi.getMySubmissions(),
            reviewIncentivesApi.getMyCredits(),
            reviewIncentivesApi.canSubmit(),
            reviewIncentivesApi.getMyEligibleWorkspaces(),
          ]);
          setReviewSubmissions(subs);
          setReviewCredits(credits);
          setCanSubmitReview(canSub);
          setEligibleWorkspaces(workspaces);
        } catch (err) {
          // non-fatal — Earn tab will show stale/empty data rather than breaking the page
        }
      }

      if (wsResponse.data?.lifetime_code_count > 0) {
        try {
          const codesResponse = await apiService.get(`/tier-access/lifetime/summary/${workspaceId}`);
          setRedeemedCodes(codesResponse.data?.summary?.codes || []);
        } catch (err) {
          console.error('Failed to load lifetime codes', err);
        }
      }
    } catch (err: any) {
      setLoadError(err.message || 'Failed to load subscription');
    } finally {
      setLoading(false);
    }
  };

  const handleReactivate = async () => {
    setLoading(true);
    try {
      const result = await subscriptionApi.reactivateSubscription(workspaceId);
      if (result.requiresCheckout) {
        showSuccess('Redirecting to checkout to reactivate your subscription...');
        setActiveTab('change');
      } else {
        showSuccess('Subscription reactivated');
        await loadData();
      }
    } catch (err: any) {
      showError(err.message || 'Failed to reactivate subscription');
    } finally {
      setLoading(false);
    }
  };

  const handleRedeemLifetime = async () => {
    if (!lifetimeCode.trim()) return;

    setLifetimeLoading(true);
    setLifetimeError(null);
    try {
      await subscriptionApi.redeemLifetimeDeal(workspaceId, lifetimeCode);
      showSuccess('Lifetime deal redeemed successfully');
      await loadData();
      setLifetimeCode('');
    } catch (err: any) {
      setLifetimeError(err.message || 'Failed to redeem code');
    } finally {
      setLifetimeLoading(false);
    }
  };

  const handleExtendTrial = async () => {
    setExtendLoading(true);
    try {
      const result = await subscriptionApi.extendTrial(workspaceId);
      await loadData();
      const newEndDate = new Date(result.workspace.trial_ends_at).toLocaleDateString();
      showSuccess(`Trial extended! Your new trial ends on ${newEndDate}.`);
    } catch (err: any) {
      showError(err.message || 'Failed to extend trial');
    } finally {
      setExtendLoading(false);
    }
  };

  // Derived state
  const canRedeem = subscription && subscriptionApi.canRedeemLifetimeDeal(subscription.status);
  const hasPaymentMethod = subscription?.has_payment_method;
  const trialDaysRemaining = subscription?.trial?.days_remaining ?? 0;
  const currentPeriodEnd = subscription?.current_period?.end;
  const billingProfile = subscription?.billing_profile;
  const isTrial = subscription?.status === SUBSCRIPTION_STATUS.TRIAL || subscription?.status === SUBSCRIPTION_STATUS.TRIAL_EXPIRED || subscription?.status === SUBSCRIPTION_STATUS.TRANSFER_TRIAL;
  const isTrialExpired = subscription?.status === SUBSCRIPTION_STATUS.TRIAL_EXPIRED;
  const isTransferTrial = subscription?.status === SUBSCRIPTION_STATUS.TRANSFER_TRIAL;
  const isLifetimeOnly = subscription?.status === SUBSCRIPTION_STATUS.LIFETIME;
  const hasLifetimeEntitlements = ((workspace?.lifetime_code_count) || 0) > 0;
  const isCancelled = subscription?.status === SUBSCRIPTION_STATUS.CANCELLED || subscription?.status === SUBSCRIPTION_STATUS.PENDING_DELETION;
  const effectiveLicense = subscription?.effective_license as any || {};
  const features = effectiveLicense.features || {};

  // Status indicator component
  const StatusDot: React.FC<{ status: string }> = ({ status }) => {
    const config: Record<string, { color: string; label: string }> = {
      active: { color: 'var(--v2-success)', label: 'Active' },
      trial: { color: '#3b82f6', label: 'Trial' },
      trial_expired: { color: 'var(--v2-error)', label: 'Expired' },
      transfer_trial: { color: '#f59e0b', label: 'Transfer Trial' },
      cancelled: { color: 'var(--v2-text-muted)', label: 'Cancelled' },
      pending_deletion: { color: 'var(--v2-error)', label: 'Pending Deletion' },
      past_due: { color: 'var(--v2-warning)', label: 'Past Due' },
      lifetime: { color: '#8b5cf6', label: 'Lifetime' },
    };
    const cfg = config[status] || { color: 'var(--v2-text-muted)', label: status };
    return (
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
        <span
          style={{
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            backgroundColor: cfg.color,
            flexShrink: 0,
          }}
        />
        <span style={{ fontSize: '0.875rem', color: 'var(--v2-text-secondary)', fontWeight: 500 }}>
          {cfg.label}
        </span>
      </div>
    );
  };

  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
          backgroundColor: 'var(--v2-bg)',
        }}
      >
        <div
          style={{
            width: '32px',
            height: '32px',
            border: '2px solid var(--v2-border)',
            borderTopColor: 'var(--v2-primary)',
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite',
          }}
        />
      </div>
    );
  }

  return (
    <div style={{ padding: '32px 40px', backgroundColor: 'var(--v2-bg)', minHeight: '100vh', fontFamily: 'var(--font-sans)' }}>
      {/* Breadcrumb */}
      <nav style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '24px' }}>
        <span
          onClick={() => navigate('/dashboard')}
          style={{
            color: 'var(--v2-primary)',
            cursor: 'pointer',
            fontSize: '0.875rem',
            fontWeight: 500,
          }}
          onMouseEnter={(e) => (e.currentTarget.style.textDecoration = 'underline')}
          onMouseLeave={(e) => (e.currentTarget.style.textDecoration = 'none')}
        >
          Workspaces
        </span>
        <ChevronRight size={14} strokeWidth={1.5} color="var(--v2-text-muted)" />
        <span style={{ color: 'var(--v2-text)', fontSize: '0.875rem', fontWeight: 500 }}>
          {workspace?.name}
        </span>
        <ChevronRight size={14} strokeWidth={1.5} color="var(--v2-text-muted)" />
        <span style={{ color: 'var(--v2-text-tertiary)', fontSize: '0.875rem' }}>
          Subscription
        </span>
      </nav>

      {/* Page Header */}
      <div style={{ marginBottom: '24px' }}>
        <h1
          style={{
            fontSize: '1.5rem',
            fontWeight: 600,
            color: 'var(--v2-text)',
            margin: '0 0 4px 0',
            letterSpacing: '-0.01em',
          }}
        >
          Subscription
        </h1>
        <p style={{ fontSize: '0.875rem', color: 'var(--v2-text-tertiary)', margin: 0 }}>
          Manage plan and billing for <strong style={{ fontWeight: 500, color: 'var(--v2-text-secondary)' }}>{workspace?.name}</strong>
        </p>
      </div>

      {/* Error Alert */}
      {loadError && (
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '12px',
            padding: '14px 16px',
            backgroundColor: 'var(--v2-error-bg)',
            borderRadius: 'var(--radius-md)',
            border: '1px solid #fecaca',
            marginBottom: '24px',
          }}
        >
          <AlertTriangle size={18} strokeWidth={1.5} color="var(--v2-error)" style={{ flexShrink: 0, marginTop: '1px' }} />
          <span style={{ fontSize: '0.875rem', color: '#991b1b' }}>{loadError}</span>
        </div>
      )}

      {/* Main Card with Tabs */}
      <div
        style={{
          backgroundColor: 'var(--v2-surface)',
          borderRadius: 'var(--radius-lg)',
          border: '1px solid var(--v2-border)',
          overflow: 'hidden',
        }}
      >
        {/* Tab Navigation */}
        <div
          style={{
            display: 'flex',
            borderBottom: '1px solid var(--v2-border)',
            backgroundColor: 'var(--v2-surface)',
          }}
        >
{[
{ id: 'plan', label: 'Plan & Usage', disabled: false },
{ id: 'usage', label: 'Usage', disabled: false },
            ...(canManageSubscription && hasLifetimeEntitlements ? [{ id: 'addons', label: 'Lifetime Add-ons' }] : []),
            ...(canManageSubscription ? [{ id: 'change', label: 'Change Plan', disabled: isSoftLaunch && !hasLifetimeEntitlements && subscription?.status === SUBSCRIPTION_STATUS.TRIAL }] : []),
          ].map((tab) => (
            <button
              key={tab.id}
onClick={() => !tab.disabled && setActiveTab(tab.id as 'plan' | 'usage' | 'change' | 'addons')}
              onMouseEnter={() => setHoveredTab(tab.id)}
              onMouseLeave={() => setHoveredTab(null)}
              disabled={tab.disabled}
              title={tab.disabled ? 'Available after Early Access period' : undefined}
              style={{
                padding: '14px 24px',
                fontSize: '0.875rem',
                fontWeight: 500,
                color: tab.disabled ? 'var(--v2-text-muted)' : activeTab === tab.id ? 'var(--v2-primary)' : hoveredTab === tab.id ? 'var(--v2-text-secondary)' : 'var(--v2-text-tertiary)',
                backgroundColor: 'transparent',
                border: 'none',
                borderBottom: activeTab === tab.id ? '2px solid var(--v2-primary)' : '2px solid transparent',
                cursor: tab.disabled ? 'not-allowed' : 'pointer',
                marginBottom: '-1px',
                transition: 'color 0.15s ease',
                opacity: tab.disabled ? 0.5 : 1,
              }}
            >
              {tab.label}
              {tab.disabled && <span style={{ marginLeft: '6px', fontSize: '0.6875rem' }}>(Coming Soon)</span>}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div style={{ padding: '24px' }}>
          {/* PLAN & USAGE TAB */}
          {activeTab === 'plan' && (
            <div>
{/* Status Alerts */}
              {/* Early Access Banner */}
{isSoftLaunch && !hasLifetimeEntitlements && subscription?.status === SUBSCRIPTION_STATUS.TRIAL && (
                <div style={{
                  background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                  border: '1px solid #fcd34d',
                  borderRadius: 'var(--radius-md)',
                  padding: '16px 20px',
                  marginBottom: '24px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: '12px',
                }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                    <Gift size={20} strokeWidth={1.5} color="#d97706" style={{ flexShrink: 0, marginTop: '2px' }} />
                    <div>
                      <p style={{ fontSize: '0.9375rem', fontWeight: 600, color: '#92400e', margin: '0 0 4px 0' }}>
                        {softLaunchMessaging.title}
                      </p>
                      <p style={{ fontSize: '0.8125rem', color: '#a16207', margin: 0 }}>
                        {trialDaysRemaining} days remaining • Full access to all features
                      </p>
                    </div>
                  </div>
                  <div style={{
                    backgroundColor: 'white',
                    padding: '6px 12px',
                    borderRadius: 'var(--radius-sm)',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    color: '#92400e',
                  }}>
                    No payment required
                  </div>
                </div>
              )}
{!isSoftLaunch && subscription?.status === SUBSCRIPTION_STATUS.TRIAL && (
  <Alert
    type="warning"
    icon={<Rocket size={18} strokeWidth={1.5} />}
    title={`Trial ends in ${trialDaysRemaining} days`}
    message={
      !billingProfile
        ? 'Assign a billing account to subscribe.'
        : hasPaymentMethod
        ? 'Convert now to keep your access.'
        : 'Add a payment method to subscribe.'
    }
actions={canManageSubscription ? (
      <>
        {!billingProfile ? (
          <AlertButton primary onClick={() => setAssignBillingModalOpen(true)}>
            Assign Billing Account
          </AlertButton>
        ) : hasPaymentMethod ? (
          <AlertButton primary onClick={() => setActiveTab('change')}>
            Subscribe Now
          </AlertButton>
        ) : (
          <AlertButton primary onClick={() => navigate('/dashboard/billing')}>
            Add Payment Method
          </AlertButton>
        )}
        {subscription.trial?.show_extend_button && (
          <AlertButton onClick={handleExtendTrial} loading={extendLoading}>
            Extend {subscription.trial?.extension_days ?? 7} Days
          </AlertButton>
        )}
      </>
    ) : undefined}
  />
)}

{isTransferTrial && (
  <Alert
    type="warning"
    icon={<ArrowRightLeft size={18} strokeWidth={1.5} />}
    title={`Set up billing within ${trialDaysRemaining} days`}
    message={
      !billingProfile
        ? "This workspace was transferred to you. Assign a billing account to continue."
        : hasPaymentMethod
        ? "This workspace was transferred to you. Subscribe to continue using it."
        : "This workspace was transferred to you. Add a payment method and subscribe to continue."
    }
actions={canManageSubscription ? (
      <>
        {!billingProfile ? (
          <AlertButton primary onClick={() => setAssignBillingModalOpen(true)}>
            Assign Billing Account
          </AlertButton>
        ) : hasPaymentMethod ? (
          <AlertButton primary onClick={() => setActiveTab('change')}>
            Subscribe Now
          </AlertButton>
        ) : (
          <AlertButton primary onClick={() => navigate('/dashboard/billing')}>
            Add Payment Method
          </AlertButton>
        )}
      </>
    ) : undefined}
  />
)}

              {isTrialExpired && (
                <Alert
                  type="error"
                  icon={<AlertTriangle size={18} strokeWidth={1.5} />}
                  title="Your trial has expired"
                  message="Subscribe now to restore access to your workspace."
actions={canManageSubscription ? (
                    <>
                      <AlertButton primary onClick={() => setActiveTab('change')}>
                        Subscribe
                      </AlertButton>
                      {subscription?.trial?.can_self_extend && (
                        <AlertButton onClick={handleExtendTrial} loading={extendLoading}>
                          Extend Trial {subscription?.trial?.extension_days ?? 7} Days
                        </AlertButton>
                      )}
                    </>
                  ) : undefined}
                />
              )}

              {isCancelled && (
                <Alert
                  type="neutral"
                  icon={<Clock size={18} strokeWidth={1.5} />}
                  title={subscription?.chargebee_subscription_id ? 'Subscription cancelled' : 'Trial cancelled'}
                  message={currentPeriodEnd ? `Access ends on ${new Date(currentPeriodEnd).toLocaleDateString()}` : undefined}
actions={canManageSubscription ? (
                    <AlertButton
                      primary
                      onClick={() => (subscription?.chargebee_subscription_id ? setReactivateModalOpen(true) : setActiveTab('change'))}
                    >
                      {subscription?.chargebee_subscription_id ? 'Reactivate' : 'Subscribe'}
                    </AlertButton>
                  ) : undefined}
                />
              )}

              {/* Current Plan Card */}
              <SectionCard>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    flexWrap: 'wrap',
                    gap: '20px',
                  }}
                >
                  <div style={{ flex: 1, minWidth: '280px' }}>
                    {/* Plan name and status */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px', flexWrap: 'wrap' }}>
                      <h3 style={{ fontSize: '1.25rem', fontWeight: 600, color: 'var(--v2-text)', margin: 0 }}>
{subscription?.tier?.tier_name
                          ? ((subscription as any)?.ltd_platform_label
                              ? `${(subscription as any).ltd_platform_label} ${subscription.tier.tier_name}`
                              : subscription.tier.tier_name)
                          : 'No Plan'}
                      </h3>
                      {hasLifetimeEntitlements && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '4px 10px', backgroundColor: '#f3e8ff', borderRadius: '6px' }}>
                          <Star size={12} strokeWidth={2} color="#8b5cf6" />
                          <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#7c3aed' }}>Lifetime</span>
                        </div>
                      )}
                      {!isLifetimeOnly && (
                        <StatusDot status={subscription?.status || 'none'} />
                      )}
                    </div>

                    {/* Pricing */}
                    {subscription?.tier && !isLifetimeOnly && (
                      <div style={{ marginBottom: '12px' }}>
                        <span style={{ fontSize: '1.75rem', fontWeight: 700, color: 'var(--v2-text)' }}>
${(() => {
                            const cbData = (subscription as any).chargebee_data;
                            // Use MRR from Chargebee - it includes coupon discounts
                            if (cbData?.mrr) {
                              // For monthly, mrr is the actual monthly charge
                              if (subscription.billing_cycle === 'monthly') {
                                return cbData.mrr.toFixed(2);
                              }
                              // For annual, mrr is monthly equivalent, multiply by 12
                              return (cbData.mrr * 12).toFixed(2);
                            }
                            // Fallback to calculated price
                            const basePrice = parseFloat(subscription.billing_cycle === 'annual' ? subscription.tier.price_annual : subscription.tier.price_monthly);
                            const addonTotal = (subscription.addons || []).reduce((sum, a) => sum + (parseFloat(a.price || '0') * (a.quantity || 1)), 0);
                            return (basePrice + addonTotal).toFixed(2);
                          })()}
                        </span>
<span style={{ fontSize: '0.875rem', color: 'var(--v2-text-tertiary)', marginLeft: '4px' }}>
                          /{subscription.billing_cycle === 'annual' ? 'year' : 'month'}
                        </span>
                        {(() => {
                          const cbData = (subscription as any).chargebee_data;
                          const rate = cbData?.recurring_tax_rate || 0;
                          if (!(rate > 0)) return null;
                          const base = cbData?.mrr
                            ? (subscription.billing_cycle === 'annual' ? cbData.mrr * 12 : cbData.mrr)
                            : parseFloat(subscription.billing_cycle === 'annual' ? subscription.tier.price_annual : subscription.tier.price_monthly);
                          return (
                            <div style={{ fontSize: '0.8125rem', color: 'var(--v2-text-tertiary)', marginTop: '2px' }}>
                              + VAT · billed ${(base * (1 + rate)).toFixed(2)}/{subscription.billing_cycle === 'annual' ? 'yr' : 'mo'} incl.
                            </div>
                          );
                        })()}
                      </div>
                    )}

                    {/* Billing/Renewal Info */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.8125rem', color: 'var(--v2-text-tertiary)' }}>
                      {isLifetimeOnly ? (
                        <>
                          <Infinity size={14} strokeWidth={1.5} />
                          <span>Never expires • {workspace?.lifetime_code_count || 0} code(s) redeemed</span>
                        </>
                      ) : subscription?.billing_cycle === 'annual' ? (
                        <>
                          <Calendar size={14} strokeWidth={1.5} />
                          <span>Annual billing • Renews {currentPeriodEnd ? new Date(currentPeriodEnd).toLocaleDateString() : 'N/A'}</span>
                        </>
                      ) : subscription?.billing_cycle === 'monthly' ? (
                        <>
                          <RefreshCw size={14} strokeWidth={1.5} />
                          <span>Monthly billing • Renews {currentPeriodEnd ? new Date(currentPeriodEnd).toLocaleDateString() : 'N/A'}</span>
                        </>
                      ) : isTransferTrial ? (
                        <>
                          <ArrowRightLeft size={14} strokeWidth={1.5} />
                          <span>Transfer trial expires {subscription?.trial?.ends_at ? new Date(subscription.trial.ends_at).toLocaleDateString() : 'soon'}</span>
                        </>
                      ) : isTrial ? (
                        <>
                          <Rocket size={14} strokeWidth={1.5} />
                          <span>Trial expires {subscription?.trial?.ends_at ? new Date(subscription.trial.ends_at).toLocaleDateString() : 'soon'}</span>
                        </>
                      ) : null}
                    </div>

                    {/* Redeemed codes for lifetime */}
                    {hasLifetimeEntitlements && redeemedCodes.length > 0 && (
                      <div style={{ marginTop: '8px', fontSize: '0.8125rem', color: 'var(--v2-text-tertiary)' }}>
                        <span>Codes: {redeemedCodes.map((c) => c.code).join(', ')}</span>
                        <span style={{ margin: '0 8px' }}>•</span>
                        <span
                          onClick={() => navigate('/redeem')}
                          style={{ color: 'var(--v2-primary)', cursor: 'pointer' }}
                          onMouseEnter={(e) => (e.currentTarget.style.textDecoration = 'underline')}
                          onMouseLeave={(e) => (e.currentTarget.style.textDecoration = 'none')}
                        >
                          Stack more codes
                        </span>
                      </div>
                    )}

                    {/* Coupon applied */}
                    {(subscription as any)?.chargebee_data?.coupons?.length > 0 && (
                      <div
                        style={{
                          marginTop: '12px',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '6px 10px',
                          backgroundColor: 'var(--v2-success-bg)',
                          borderRadius: '6px',
                        }}
                      >
                        <Gift size={12} strokeWidth={2} color="var(--v2-success-dark)" />
                        <span style={{ fontSize: '0.75rem', fontWeight: 500, color: '#166534' }}>
                          {(subscription as any).chargebee_data.coupons[0].name || (subscription as any).chargebee_data.coupons[0].coupon_id} applied
                        </span>
                      </div>
                    )}
                  </div>

{/* Action Button */}
{isSoftLaunch && isTrial && !hasLifetimeEntitlements ? (
  <div style={{
    padding: '10px 20px',
    backgroundColor: 'var(--v2-surface-alt)',
    border: '1px solid var(--v2-border)',
    borderRadius: 'var(--radius-md)',
    fontSize: '0.875rem',
    color: 'var(--v2-text-muted)',
    textAlign: 'center',
  }}>
    Plan selection available after Early Access
  </div>
) : canManageSubscription ? (
  <PrimaryButton 
    onClick={() => {
      if (isTrial && !billingProfile) {
        setAssignBillingModalOpen(true);
      } else {
        setActiveTab('change');
      }
    }}
  >
    {isLifetimeOnly ? 'Upgrade to Paid' : isTrial && !billingProfile ? 'Assign Billing Account' : isTrial ? 'Subscribe Now' : 'Change Plan'}
  </PrimaryButton>
) : null}
                </div>
              </SectionCard>

              {/* License Allocations */}
              <SectionHeader icon={<Package size={16} strokeWidth={1.5} />} title="License Allocations" />
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                  gap: '12px',
                  marginBottom: '32px',
                }}
              >
                {[
		  { label: 'Monthly Active Users', value: effectiveLicense?.named_users ?? 0, icon: Users },
                  { label: 'Public View Users', value: effectiveLicense?.public_view_users ?? 0, icon: Eye },
                  { label: 'API Endpoints', value: effectiveLicense?.api_endpoints ?? 0, icon: Plug },
                  { label: 'Scheduled Jobs', value: effectiveLicense?.scheduled_jobs ?? 0, icon: Clock },
                  { label: 'Custom Domains', value: effectiveLicense?.custom_domains ?? 0, icon: Globe },
		  { label: 'Branding Policies', value: (effectiveLicense?.branding_policies ?? 0) === -1 ? 'Unlimited' : effectiveLicense?.branding_policies ?? 0, icon: Palette },
                  { label: 'ODBC Drivers (Tier 1)', value: (effectiveLicense?.odbc_tier1_drivers ?? 0) === -1 ? 'Unlimited' : effectiveLicense?.odbc_tier1_drivers ?? 0, icon: Database },                ].map((item) => {
                  const Icon = item.icon;
                  return (
                    <div
                      key={item.label}
                      style={{
                        backgroundColor: 'var(--v2-surface-alt)',
                        borderRadius: 'var(--radius-md)',
                        padding: '16px',
                        textAlign: 'center',
                        border: '1px solid var(--v2-border-light)',
                      }}
                    >
                      <Icon size={20} strokeWidth={1.5} color="var(--v2-text-muted)" style={{ marginBottom: '8px' }} />
                      <div style={{ fontSize: '1.5rem', fontWeight: 600, color: 'var(--v2-text)', marginBottom: '4px' }}>{item.value}</div>
                      <div style={{ fontSize: '0.6875rem', fontWeight: 500, color: 'var(--v2-text-muted)', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                        {item.label}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Included Features */}
              <SectionHeader icon={<Check size={16} strokeWidth={2} />} title="Included Features" />
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: '6px',
                  marginBottom: '32px',
                }}
              >
                {Object.entries(FEATURE_NAMES).map(([code, name]) => {
                  const isEnabled = features[code] === true;
                  return (
                    <div
                      key={code}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        padding: '10px 12px',
                        backgroundColor: isEnabled ? 'var(--v2-success-bg)' : 'var(--v2-surface-alt)',
                        borderRadius: 'var(--radius-sm)',
                        opacity: isEnabled ? 1 : 0.5,
                      }}
                    >
                      {isEnabled ? (
                        <Check size={14} strokeWidth={2.5} color="var(--v2-success-dark)" />
                      ) : (
                        <X size={14} strokeWidth={2} color="var(--v2-text-muted)" />
                      )}
                      <span
                        style={{
                          fontSize: '0.8125rem',
                          fontWeight: isEnabled ? 500 : 400,
                          color: isEnabled ? 'var(--v2-text)' : 'var(--v2-text-muted)',
                        }}
                      >
                        {name}
                      </span>
                    </div>
                  );
                })}
              </div>

              {/* Active Add-ons */}
              {subscription?.addons && subscription.addons.length > 0 && (
                <>
                  <SectionHeader icon={<Package size={16} strokeWidth={1.5} />} title="Active Add-ons" />
                  <div
                    style={{
                      border: '1px solid var(--v2-border)',
                      borderRadius: 'var(--radius-md)',
                      overflow: 'hidden',
                      marginBottom: '32px',
                    }}
                  >
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr>
                          <th style={tableHeaderStyle}>Add-on</th>
                          <th style={{ ...tableHeaderStyle, textAlign: 'center' }}>Quantity</th>
                          <th style={{ ...tableHeaderStyle, textAlign: 'right' }}>Price</th>
                        </tr>
                      </thead>
                      <tbody>
                        {subscription.addons.map((addon) => (
                          <tr key={addon.addon_id} style={{ borderTop: '1px solid var(--v2-border-light)' }}>
                            <td style={tableCellStyle}>{addon.addon_name}</td>
                            <td style={{ ...tableCellStyle, textAlign: 'center', color: 'var(--v2-text-tertiary)' }}>{addon.quantity}</td>
                            <td style={{ ...tableCellStyle, textAlign: 'right', fontWeight: 500 }}>
                              ${parseFloat(addon.price || '0').toFixed(2)}/{subscription.billing_cycle === 'annual' ? 'yr' : 'mo'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}

              {/* Billing Account */}
              {!isLifetimeOnly && (
                <>
                  <SectionHeader icon={<CreditCard size={16} strokeWidth={1.5} />} title="Billing Account" />
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      backgroundColor: 'var(--v2-surface-alt)',
                      border: '1px solid var(--v2-border)',
                      borderRadius: 'var(--radius-md)',
                      padding: '20px',
                      flexWrap: 'wrap',
                      gap: '16px',
                      marginBottom: '32px',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                      <div
                        style={{
                          width: '44px',
                          height: '44px',
                          borderRadius: 'var(--radius-md)',
                          backgroundColor: billingProfile ? 'var(--v2-primary-light)' : 'var(--v2-surface-hover)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <CreditCard size={20} strokeWidth={1.5} color={billingProfile ? 'var(--v2-primary)' : 'var(--v2-text-muted)'} />
                      </div>
                      <div>
                        {billingProfile ? (
                          <>
                            <p style={{ fontSize: '0.9375rem', fontWeight: 500, color: 'var(--v2-text)', margin: '0 0 4px 0' }}>
                              {billingProfile.name}
                            </p>
                            {(billingProfile as any).company_name && (billingProfile as any).company_name !== billingProfile.name && (
                              <p style={{ fontSize: '0.8125rem', color: 'var(--v2-text-tertiary)', margin: '0 0 8px 0' }}>
                                {(billingProfile as any).company_name}
                              </p>
                            )}
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <span
                                style={{
                                  width: '6px',
                                  height: '6px',
                                  borderRadius: '50%',
                                  backgroundColor: hasPaymentMethod ? 'var(--v2-success)' : 'var(--v2-warning)',
                                }}
                              />
                              <span style={{ fontSize: '0.75rem', color: hasPaymentMethod ? 'var(--v2-success-dark)' : 'var(--v2-warning-dark)' }}>
                                {hasPaymentMethod ? 'Payment method on file' : 'No payment method'}
                              </span>
                            </div>
                          </>
                        ) : (
                          <p style={{ fontSize: '0.875rem', color: 'var(--v2-text-tertiary)', margin: 0 }}>No billing account assigned</p>
                        )}
                      </div>
                    </div>

<div style={{ display: 'flex', gap: '10px' }}>
                      {canManageSubscription && (
                        <SecondaryButton
                          onClick={() => setAssignBillingModalOpen(true)}
                          disabled={!!subscription?.chargebee_subscription_id && ['active', 'past_due'].includes(subscription?.status || '')}
                        >
                          {billingProfile ? 'Change' : 'Assign'}
                        </SecondaryButton>
                      )}
                      {canManageSubscription && (
                        <GhostButton onClick={() => navigate('/dashboard/billing')}>
                          Manage Billing <ArrowRight size={14} strokeWidth={1.5} />
                        </GhostButton>
                      )}
                    </div>
                  </div>
                </>
              )}

              {/* Lifetime Deal Redemption */}
{canRedeem && (workspace?.lifetime_code_count || 0) > 0 && (
                <>
                  <SectionHeader icon={<Ticket size={16} strokeWidth={1.5} />} title="Lifetime Deal" />
                  <div
                    style={{
                      backgroundColor: 'var(--v2-surface-alt)',
                      border: '1px solid var(--v2-border)',
                      borderRadius: 'var(--radius-md)',
                      padding: '20px',
                      marginBottom: '32px',
                    }}
                  >
                    <p style={{ fontSize: '0.875rem', color: 'var(--v2-text)', margin: '0 0 12px 0' }}>
                      Have a lifetime deal code? Enter it below to redeem.
                    </p>
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                      <input
                        type="text"
                        value={lifetimeCode}
                        onChange={(e) => setLifetimeCode(e.target.value.toUpperCase())}
                        placeholder="Enter code"
                        disabled={lifetimeLoading}
                        style={{
                          padding: '10px 14px',
                          backgroundColor: 'var(--v2-surface)',
                          border: '1px solid var(--v2-border)',
                          borderRadius: 'var(--radius-sm)',
                          fontSize: '0.875rem',
                          fontFamily: 'var(--font-mono)',
                          width: '200px',
                          outline: 'none',
                          transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
                        }}
                        onFocus={(e) => {
                          e.currentTarget.style.borderColor = 'var(--v2-primary)';
                          e.currentTarget.style.boxShadow = '0 0 0 3px var(--v2-primary-muted)';
                        }}
                        onBlur={(e) => {
                          e.currentTarget.style.borderColor = 'var(--v2-border)';
                          e.currentTarget.style.boxShadow = 'none';
                        }}
                      />
                      <PrimaryButton onClick={handleRedeemLifetime} disabled={lifetimeLoading || !lifetimeCode.trim()}>
                        {lifetimeLoading ? 'Redeeming...' : 'Redeem'}
                      </PrimaryButton>
                    </div>
                    {lifetimeError && (
                      <p style={{ fontSize: '0.8125rem', color: 'var(--v2-error)', margin: '8px 0 0 0' }}>{lifetimeError}</p>
                    )}
                  </div>
                </>
              )}

              {/* Cancel Section */}
{canManageSubscription && !(isSoftLaunch && isTrial && !hasLifetimeEntitlements) && [SUBSCRIPTION_STATUS.ACTIVE, SUBSCRIPTION_STATUS.TRIAL, SUBSCRIPTION_STATUS.TRANSFER_TRIAL, SUBSCRIPTION_STATUS.PAST_DUE].includes(subscription?.status as any) &&
                (!isLifetimeOnly || subscription?.chargebee_subscription_id) && (
                  <div style={{ borderTop: '1px solid var(--v2-border)', paddingTop: '24px' }}>
                    <button
                      onClick={() => setCancelModalOpen(true)}
                      style={{
                        padding: '10px 20px',
                        backgroundColor: 'transparent',
                        color: 'var(--v2-error)',
                        border: 'none',
                        borderRadius: 'var(--radius-sm)',
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        cursor: 'pointer',
                        transition: 'background-color 0.15s ease',
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--v2-error-bg)')}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                    >
                      {subscription?.status === 'trial' || subscription?.status === 'transfer_trial' ? 'Cancel Trial' : 'Cancel Subscription'}
                    </button>
                  </div>
                )}
            </div>
          )}

{/* LIFETIME ADD-ONS TAB */}
          {activeTab === 'usage' && (
            <MAUUsage
              fetchUsage={() => subscriptionApi.getMauUsage(workspaceId)}
              isLifetime={hasLifetimeEntitlements}
              subscriptionStatus={subscription?.status || 'none'}
            />
          )}

          {activeTab === 'addons' && hasLifetimeEntitlements && (
            <LifetimeAddonStoreCore
              availableAddons={lifetimeAddons}
              myAddons={myLifetimeAddons}
              loading={addonsLoading}
              hasPaymentMethod={!!hasPaymentMethod}
              billingProfile={billingProfile || null}
onEstimate={(addonId, quantity) => subscriptionApi.estimateLifetimeAddon(workspaceId, addonId, quantity)}
onPurchase={async (addonId, quantity) => {
                // 3DS: authorize the tax-inclusive total on-session for EEA/Stripe cards
                // before charging. Authnet/US returns gateway:'none' and falls through off-session.
                const ensureStripe = (pk: string): Promise<any> => new Promise((resolve, reject) => {
                  const w = window as any;
                  if (w.Stripe) { resolve(w.Stripe(pk)); return; }
                  const existing = document.querySelector('script[src="https://js.stripe.com/v3/"]');
                  const onload = () => resolve((window as any).Stripe(pk));
                  if (existing) { existing.addEventListener('load', onload); return; }
                  const s = document.createElement('script');
                  s.src = 'https://js.stripe.com/v3/';
                  s.onload = onload;
                  s.onerror = () => reject(new Error('Failed to load Stripe.js'));
                  document.head.appendChild(s);
                });
// One idempotency key + PI per attempt; reused across an ambiguous retry
                // so the charge can't double, reset on a definitive decline or success.
                const idempotency_key = addonAttempt.beginKey();
                try {
                  let paymentIntentId: string | undefined = addonAttempt.getPI();
                  if (!paymentIntentId) {
                    const intent = await subscriptionApi.createLifetimeAddonPaymentIntent(workspaceId, addonId, quantity);
                    if (intent && intent.gateway === 'stripe') {
                      paymentIntentId = intent.paymentIntentId;
                      if (intent.requiresAction) {
                        const stripe = await ensureStripe(intent.stripePublishableKey);
                        const result = await stripe.confirmCardPayment(intent.clientSecret);
                        if (result.error) {
                          throw new Error(result.error.message || 'Card authentication failed');
                        }
                      }
                    }
                    addonAttempt.setPI(paymentIntentId);
                  }
                  await subscriptionApi.purchaseLifetimeAddon(workspaceId, addonId, quantity, paymentIntentId, idempotency_key);
                  addonAttempt.succeed();
                } catch (err) {
                  addonAttempt.fail(err);
                  throw err;
                }
              const [available, mine] = await Promise.all([
                subscriptionApi.listLifetimeAddons(workspaceId),
                subscriptionApi.getMyLifetimeAddons(workspaceId),
              ]);
              setLifetimeAddons(available);
              setMyLifetimeAddons(mine);
                await loadData();
              }}
              onRefund={async (workspaceAddonId) => {
await subscriptionApi.refundLifetimeAddon(workspaceId, workspaceAddonId);
              const [available, mine] = await Promise.all([
                subscriptionApi.listLifetimeAddons(workspaceId),
                subscriptionApi.getMyLifetimeAddons(workspaceId),
              ]);
              setLifetimeAddons(available);
              setMyLifetimeAddons(mine);
                await loadData();
              }}
              onSuccess={(msg) => showSuccess(msg)}
              onError={(msg) => showError(msg)}
              mode="embedded"
              reviewSubmissions={reviewSubmissions}
              reviewCredits={reviewCredits}
              canSubmitReview={canSubmitReview}
              myWorkspaces={eligibleWorkspaces}
              onSubmitReview={async (platform, url, screenshotUrl) => {
                await reviewIncentivesApi.submit(platform, url, screenshotUrl);
                await loadData();
              }}
              onRedeemCredit={async (creditId, targetWorkspaceId) => {
                await reviewIncentivesApi.redeemCredit(creditId, targetWorkspaceId);
                await loadData();
              }}
            />
          )}

          {/* CHANGE PLAN TAB */}
          {activeTab === 'change' && (
<TierConfigurator
              workspaceId={workspaceId}
              parityDiscountPct={(subscription as any)?.parity_discount_pct ?? null}
              parityCountry={(subscription as any)?.parity_country ?? null}
              campaign={myCampaign}
              currentTierId={subscription?.tier?.id}
              currentBillingCycle={subscription?.billing_cycle || 'monthly'}
              currentAddons={(subscription?.addons || []).map((a) => ({
                addon_code: a.addon_code,
                quantity: a.quantity,
              }))}
              currentPromoCode={(subscription as any).chargebee_data?.coupons?.[0]?.coupon_id || null}
              isLifetime={hasLifetimeEntitlements}
lifetimeStackedValues={
                hasLifetimeEntitlements
                  ? {
                      named_users: (effectiveLicense as any)?.named_users || 0,
                      public_view_users: (effectiveLicense as any)?.public_view_users || 0,
                      api_endpoints: (effectiveLicense as any)?.api_endpoints || 0,
                      custom_domains: (effectiveLicense as any)?.custom_domains || 0,
                      branding_policies: (effectiveLicense as any)?.branding_policies || 0,
                    }
                  : undefined
              }
              onConfirm={(config) => {
                navigate(`/dashboard/workspace/${workspaceId}/checkout`, {
                  state: {
                    tier: config.tier,
                    billingCycle: config.billingCycle,
                    addons: config.addons,
                    estimate: config.estimate,
                  },
                });
              }}
              confirmButtonText={isTrial ? 'Continue to Checkout' : isLifetimeOnly ? 'Upgrade to Paid Plan' : 'Review Changes'}
              disabled={false}
            />
          )}
        </div>
      </div>

      {/* Modals */}
      <CancelSubscriptionModal
        open={cancelModalOpen}
        onClose={() => setCancelModalOpen(false)}
        workspaceId={workspaceId}
        workspaceName={workspace?.name || ''}
        currentPeriodEnd={currentPeriodEnd || null}
        subscriptionStatus={subscription?.status || 'none'}
        onCancelled={loadData}
        isLifetime={hasLifetimeEntitlements}
        lifetimeTierName={workspace?.lifetime_fallback_tier_name || 'Lifetime'}
        lifetimeNamedUsers={workspace?.lifetime_stacked_named_users || 0}
        lifetimePublicViewUsers={workspace?.lifetime_stacked_public_view_users || 0}
      />

      <ReactivateSubscriptionModal
        open={reactivateModalOpen}
        onClose={() => setReactivateModalOpen(false)}
        workspaceName={workspace?.name || ''}
        hasChargebeeSubscription={!!subscription?.chargebee_subscription_id}
        currentPeriodEnd={currentPeriodEnd || null}
        onConfirm={handleReactivate}
      />

      <AssignBillingProfileModal
        open={assignBillingModalOpen}
        onClose={() => setAssignBillingModalOpen(false)}
        workspaceId={workspaceId}
        workspaceName={workspace?.name || ''}
        currentBillingProfileId={workspace?.billing_profile_id || null}
        onAssigned={loadData}
      />
    </div>
  );
};

// ============================================
// REUSABLE COMPONENTS
// ============================================

// Table styles
const tableHeaderStyle: React.CSSProperties = {
  padding: '12px 16px',
  textAlign: 'left',
  fontSize: '0.75rem',
  fontWeight: 500,
  color: 'var(--v2-text-muted)',
  backgroundColor: 'var(--v2-surface-alt)',
  borderBottom: '1px solid var(--v2-border)',
};

const tableCellStyle: React.CSSProperties = {
  padding: '14px 16px',
  fontSize: '0.875rem',
  color: 'var(--v2-text)',
};

// Section Header
const SectionHeader: React.FC<{ icon: React.ReactNode; title: string }> = ({ icon, title }) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      fontSize: '0.6875rem',
      fontWeight: 500,
      color: 'var(--v2-text-muted)',
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      marginBottom: '12px',
    }}
  >
    {icon}
    {title}
  </div>
);

// Section Card
const SectionCard: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div
    style={{
      backgroundColor: 'var(--v2-surface-alt)',
      border: '1px solid var(--v2-border)',
      borderRadius: 'var(--radius-md)',
      padding: '20px',
      marginBottom: '32px',
    }}
  >
    {children}
  </div>
);

// Alert Component
const Alert: React.FC<{
  type: 'warning' | 'error' | 'neutral';
  icon: React.ReactNode;
  title: string;
  message?: string;
  actions?: React.ReactNode;
}> = ({ type, icon, title, message, actions }) => {
  const colors = {
    warning: { bg: 'var(--v2-warning-bg)', border: '#fcd34d', title: '#92400e', text: '#a16207', icon: 'var(--v2-warning-dark)' },
    error: { bg: 'var(--v2-error-bg)', border: '#fecaca', title: '#991b1b', text: '#b91c1c', icon: 'var(--v2-error)' },
    neutral: { bg: 'var(--v2-surface-alt)', border: 'var(--v2-border)', title: 'var(--v2-text-secondary)', text: 'var(--v2-text-tertiary)', icon: 'var(--v2-text-muted)' },
  };
  const c = colors[type];

  return (
    <div
      style={{
        backgroundColor: c.bg,
        borderLeft: `4px solid ${c.border}`,
        borderRadius: '0 var(--radius-md) var(--radius-md) 0',
        padding: '16px 20px',
        marginBottom: '24px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '12px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
        <span style={{ color: c.icon, marginTop: '2px' }}>{icon}</span>
        <div>
          <p style={{ fontSize: '0.9375rem', fontWeight: 500, color: c.title, margin: 0 }}>{title}</p>
          {message && <p style={{ fontSize: '0.8125rem', color: c.text, margin: '4px 0 0 0' }}>{message}</p>}
        </div>
      </div>
      {actions && <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>{actions}</div>}
    </div>
  );
};

// Alert Button
const AlertButton: React.FC<{
  children: React.ReactNode;
  onClick: () => void;
  primary?: boolean;
  loading?: boolean;
}> = ({ children, onClick, primary, loading }) => {
  const [hovered, setHovered] = useState(false);

  return (
    <button
      onClick={onClick}
      disabled={loading}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        padding: '8px 16px',
        backgroundColor: primary ? (hovered ? 'var(--v2-primary-hover)' : 'var(--v2-primary)') : hovered ? 'var(--v2-surface-hover)' : 'var(--v2-surface)',
        color: primary ? '#ffffff' : 'var(--v2-text)',
        border: primary ? 'none' : '1px solid var(--v2-border)',
        borderRadius: 'var(--radius-sm)',
        fontSize: '0.8125rem',
        fontWeight: 500,
        cursor: loading ? 'not-allowed' : 'pointer',
        opacity: loading ? 0.7 : 1,
        transition: 'all 0.15s ease',
      }}
    >
      {children}
    </button>
  );
};

// Primary Button
const PrimaryButton: React.FC<{
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
}> = ({ children, onClick, disabled }) => {
  const [hovered, setHovered] = useState(false);

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        padding: '10px 20px',
        backgroundColor: disabled ? 'var(--v2-border)' : hovered ? 'var(--v2-primary-hover)' : 'var(--v2-primary)',
        color: disabled ? 'var(--v2-text-muted)' : '#ffffff',
        border: 'none',
        borderRadius: 'var(--radius-md)',
        fontSize: '0.875rem',
        fontWeight: 500,
        cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'background-color 0.15s ease',
      }}
    >
      {children}
    </button>
  );
};

// Secondary Button
const SecondaryButton: React.FC<{
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
}> = ({ children, onClick, disabled }) => {
  const [hovered, setHovered] = useState(false);

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        padding: '8px 16px',
        backgroundColor: hovered && !disabled ? 'var(--v2-surface-hover)' : 'var(--v2-surface)',
        color: disabled ? 'var(--v2-text-muted)' : 'var(--v2-text)',
        border: '1px solid var(--v2-border)',
        borderRadius: 'var(--radius-sm)',
        fontSize: '0.875rem',
        fontWeight: 500,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        transition: 'all 0.15s ease',
      }}
    >
      {children}
    </button>
  );
};

// Ghost Button
const GhostButton: React.FC<{
  children: React.ReactNode;
  onClick: () => void;
}> = ({ children, onClick }) => {
  const [hovered, setHovered] = useState(false);

  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        padding: '8px 16px',
        backgroundColor: 'transparent',
        color: hovered ? 'var(--v2-primary-hover)' : 'var(--v2-primary)',
        border: 'none',
        borderRadius: 'var(--radius-sm)',
        fontSize: '0.875rem',
        fontWeight: 500,
        cursor: 'pointer',
        transition: 'color 0.15s ease',
      }}
    >
      {children}
    </button>
  );
};

export default SubscriptionPage;
